// Écran 5 — la fenêtre de statut : est-ce que je progresse ? Un champ manquant s'affiche
// entre crochets, jamais inventé.

import type { PropsEcran } from '../App';
import { EnTeteRetour, Fenetre, Nav } from '../components';
import type { Etat, MesureBalance } from '@alfred/shared';
import { ajouterJours, jjmm, kg, progressionPoids, tendance } from '../lib/format';
import { VOCAB } from '../lib/theme';

function prochainLundi(today: string): string {
  const jour = new Date(`${today}T12:00:00Z`).getUTCDay(); // 0 = dimanche
  return ajouterJours(today, ((8 - jour) % 7) || 7);
}

/** Une tuile de la balance : la valeur, puis la tendance depuis la mesure précédente. */
function TuileBalance({ titre, mesure, unite, progresSiBaisse }: { titre: string; mesure: MesureBalance | null; unite: string; progresSiBaisse: boolean }) {
  const t = mesure ? tendance(mesure.ecart, progresSiBaisse) : null;
  return (
    <div className="tuile">
      <small>{titre}</small>
      <b>{mesure ? `${mesure.valeur.toFixed(1).replace('.', ',')} ${unite}` : '—'}</b>
      {!mesure && <span>[ en attente de la balance ]</span>}
      {mesure && !t && <span>première mesure, {jjmm(mesure.date)}</span>}
      {mesure && t && mesure.depuis && <span className={`tendance ${t.sens}`}>{t.texte} depuis le {jjmm(mesure.depuis)}</span>}
    </div>
  );
}

export function Statut({ etat }: PropsEcran) {
  const { profil, statut } = etat;

  return (
    <main className="ecran avec-nav">
      <EnTeteRetour titre={VOCAB.statut} />
      <Fenetre>
        <div className="grille2">
          <div className="champ"><small>NOM</small><span style={{ fontWeight: 700, fontSize: 15 }}>{profil.prenom}</span></div>
          <div className="champ"><small>NIVEAU</small><span className="mono accent" style={{ fontWeight: 700, fontSize: 15 }}>{statut.niveau}</span></div>
          <div className="champ"><small>CLASSE</small><span>{profil.classe || '[ À définir ]'}</span></div>
          <div className="champ"><small>TITRE</small><span className="muted">{VOCAB.aDebloquer}</span></div>
        </div>
        <PoidsStatut etat={etat} />
        <TuilesStatut etat={etat} />
        <a className="btn large" href="#/pesee" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>Saisir une pesée</a>
      </Fenetre>

      {statut.debuffs.length > 0 && <span className="display" style={{ fontSize: 14, letterSpacing: 3 }}>{VOCAB.debuffs}</span>}
      <DebuffsStatut etat={etat} />
      {(etat.phase?.semaines ?? 0) > 0 && <span className="display" style={{ fontSize: 14, letterSpacing: 3 }}>{VOCAB.progression}</span>}
      <ProgressionStatut etat={etat} />
      <Nav actif="statut" />
    </main>
  );
}

// Les blocs de la fenêtre, repris tels quels par le tableau de bord du bureau.

export function PoidsStatut({ etat }: { etat: Etat }) {
  const { profil, statut } = etat;
  const progression = progressionPoids(statut.poidsDepart, statut.poidsActuel, profil.objectifPoids);
  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, gap: 8 }}>
        <span style={{ letterSpacing: 2 }}>POIDS</span>
        <span className="mono">
          {kg(statut.poidsActuel)} → {profil.objectifPoids ? `${profil.objectifPoids} kg${profil.objectifPoidsDate ? ` · ${jjmm(profil.objectifPoidsDate)}` : ''}` : '[OBJECTIF À DÉFINIR]'}
        </span>
      </div>
      <div className="jauge" style={{ height: 8 }}><div style={{ width: `${Math.max(2, progression * 100)}%` }} /></div>
      <span className="muted" style={{ fontSize: 11 }}>
        Départ {kg(statut.poidsDepart)} · prochaine pesée lun. {jjmm(etat.peseeDue ? etat.date : prochainLundi(etat.date))}, à jeun
      </span>
    </div>
  );
}

export function TuilesStatut({ etat }: { etat: Etat }) {
  const { statut } = etat;
  return (
    <>
      {(statut.masseGrasse || statut.masseMusculaire) && (
        <div className="grille2" style={{ gap: 8 }}>
          <TuileBalance titre="MASSE GRASSE" mesure={statut.masseGrasse} unite="%" progresSiBaisse />
          <TuileBalance titre="MUSCLE" mesure={statut.masseMusculaire} unite="kg" progresSiBaisse={false} />
        </div>
      )}
      <div className="grille2" style={{ gap: 8 }}>
        <div className="tuile"><small>FORCE</small><b>{statut.force}</b><span>séances faites</span></div>
        <div className="tuile"><small>ENDURANCE</small><b>{statut.enduranceMin}</b><span>min de tapis cette semaine</span></div>
        <div className="tuile"><small>RÉGULARITÉ</small><b>{statut.regularite}</b><span>semaines tenues</span></div>
        <div className="tuile"><small>VITALITÉ</small><b>{statut.vitalite.toFixed(1).replace('.', ',')}</b><span>kg perdus</span></div>
      </div>
    </>
  );
}

export function DebuffsStatut({ etat }: { etat: Etat }) {
  return (
    <>
      {etat.statut.debuffs.map((d) => (
        <div key={d.titre} className="debuff"><b>{d.titre}</b><span>{d.consigne}</span></div>
      ))}
    </>
  );
}

/** Les semaines de la phase ; `legende` : la phrase d'explication dessous (le bureau s'en passe). */
export function ProgressionStatut({ etat, legende = true }: { etat: Etat; legende?: boolean }) {
  const phase = etat.phase;
  const semaines = phase?.semaines ?? 0;
  if (!phase || semaines === 0) return null;
  return (
    <>
      <div className="semaines">
        {Array.from({ length: semaines }, (_, k) => (
          <div key={k} className={k + 1 === phase.semaineCourante ? 'courante' : k + 1 < phase.semaineCourante ? 'passee' : ''}>S{k + 1}</div>
        ))}
        <div className="suite">PH. {phase.numero + 1}</div>
      </div>
      {legende && (
        <span className="muted" style={{ fontSize: 12 }}>
          Phase {phase.numero} · {phase.nom} · {phase.semaineCourante ? `semaine ${phase.semaineCourante} sur ${semaines}` : `début le ${jjmm(phase.debut)}`}.
          La version 12 min compte comme une séance faite.
        </span>
      )}
    </>
  );
}
